import type { Resolver } from 'react-hook-form'
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { addDays, format, parseISO } from 'date-fns'
import {
  Modal,
  Button,
  ConfirmDialog,
  Input,
  Select,
  TimeInput,
  Toggle,
  DateInput,
} from '@/components/ui'
import type { AxiosError } from 'axios'
import { toast } from 'sonner'
import { Plus, Trash2 } from 'lucide-react'
import { Avatar } from '@/components/common/Avatar'
import {
  useShiftTemplates,
  useCreateShiftAssignment,
  useRecentCustomShiftSlots,
  useBulkAssignShifts,
} from '../hooks/useShifts'
import { useCompanyMembers, useMyCompanies } from '@/features/companies/hooks/useCompanies'
import {
  getMemberId,
  getMemberLabel,
  getMemberSubline,
} from '@/features/companies/utils/memberLabel'
import {
  createShiftAssignmentSchema,
  type CreateShiftAssignmentFormData,
} from '../schemas/shift.schemas'
import type { BulkAssignShiftResult, RecentCustomShiftSlot } from '@/types/shift.types'
import { EMPLOYEE_NOT_ELIGIBLE, summarizeIneligibleBulkErrors } from '../utils/shiftEligibility'
import { useCurrentOrganization } from '@/features/organization/hooks/useCurrentOrganization'

const WEEKDAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const

function formatPrefillDate(dateStr: string): string {
  try {
    return format(parseISO(dateStr), 'EEE, d MMM yyyy')
  } catch {
    return dateStr
  }
}

function toCustomSlotPayload(data: CreateShiftAssignmentFormData) {
  const breakAllowances = (data.customSlot.breakAllowances ?? []).map((item) => ({
    ...item,
    scheduledStartTime: item.scheduledStartTime?.trim() || undefined,
  }))
  return {
    name: data.customSlot.name ?? '',
    startTime: data.customSlot.startTime ?? '',
    endTime: data.customSlot.endTime ?? '',
    gracePeriodMinutes: Number(data.customSlot.gracePeriodMinutes ?? 0),
    maxEarlyClockInMinutes: Number(data.customSlot.maxEarlyClockInMinutes ?? 10),
    allowClockInPastEnd: data.customSlot.allowClockInPastEnd ?? false,
    lateClockInCutoffMinutes: Number(data.customSlot.lateClockInCutoffMinutes ?? 0),
    breakAllowances,
    color: data.customSlot.color || undefined,
    description: data.customSlot.description || undefined,
  }
}

function toDateInputValue(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function shiftStartDateTime(date: string, startTime?: string): Date | null {
  if (!date || !startTime || !HH_MM_RE.test(startTime)) return null
  const parsed = new Date(`${date}T${startTime}:00`)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

function isShiftStartInPast(date: string, startTime?: string, now = new Date()): boolean {
  const startAt = shiftStartDateTime(date, startTime)
  return startAt ? startAt.getTime() < now.getTime() : false
}

function formatRepeatPreviewDate(dateStr: string): string {
  try {
    return format(parseISO(dateStr), 'EEE d')
  } catch {
    return dateStr
  }
}

function remainingDatesThisWeek(dateStr: string): string[] {
  if (!dateStr) return []
  const start = parseISO(dateStr)
  if (Number.isNaN(start.getTime())) return []
  // Convert JavaScript's Sunday-first index to a Monday-first work-week index.
  const day = (start.getDay() + 6) % 7

  const dates: string[] = []
  for (let offset = 0; offset <= 6 - day; offset += 1) {
    dates.push(format(addDays(start, offset), 'yyyy-MM-dd'))
  }
  return dates
}

function getWeekdayName(dateStr: string): string {
  const parsed = parseISO(dateStr)
  if (Number.isNaN(parsed.getTime())) return ''
  return WEEKDAY_NAMES[parsed.getDay()]
}

function defaultRepeatDatesForWorkWeek(dates: string[], workWeek: string[]): string[] {
  if (dates.length === 0) return []
  const workWeekSet = new Set(workWeek)
  const selected = dates.filter((date) => workWeekSet.has(getWeekdayName(date)))
  if (!selected.includes(dates[0])) {
    selected.unshift(dates[0])
  }
  return selected
}

function getBulkAssignSummary(
  result: {
    created?: number
    skipped?: number
    errors?: BulkAssignShiftResult['errors']
    summary?: {
      totalRequested?: number
      totalFailed?: number
      totalSkipped?: number
    }
  },
  requestedCount: number,
  userNames: Record<string, string> = {}
): { message: string; kind: 'success' | 'warning' } {
  const created = Number(result.created ?? 0)
  const failed = Number(result.summary?.totalFailed ?? result.errors?.length ?? 0)
  const skipped = Number(result.summary?.totalSkipped ?? result.skipped ?? 0)
  const totalRequested = Number(result.summary?.totalRequested ?? requestedCount)
  const reasonSummary = [
    [
      ...new Set(
        (result.errors ?? [])
          .filter((error) => error.code !== EMPLOYEE_NOT_ELIGIBLE)
          .map((error) => error.reason)
          .filter(Boolean)
      ),
    ]
      .slice(0, 2)
      .join(' '),
    summarizeIneligibleBulkErrors(result.errors ?? [], userNames),
  ]
    .filter(Boolean)
    .join(' ')

  if (created === totalRequested && failed === 0 && skipped === 0) {
    return { message: `${created} shifts assigned`, kind: 'success' }
  }
  if (created > 0 && (failed > 0 || skipped > 0)) {
    return {
      message: `${created} of ${totalRequested} shifts assigned, ${failed + skipped} not created.${
        reasonSummary ? ` ${reasonSummary}` : ''
      }`,
      kind: 'warning',
    }
  }
  if (created > 0) {
    return { message: `${created} shifts assigned`, kind: 'success' }
  }
  return { message: 'No shifts assigned', kind: 'warning' }
}

function RecentSlotButton({
  slot,
  onApply,
}: {
  slot: RecentCustomShiftSlot
  onApply: (slot: RecentCustomShiftSlot) => void
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={() => onApply(slot)}
      className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-left transition hover:border-amber-300 hover:bg-amber-100 dark:border-amber-800/70 dark:bg-amber-950/40 dark:hover:bg-amber-900/40"
    >
      <div className="flex items-center justify-between gap-3">
        <span className="truncate text-sm font-medium text-amber-950 dark:text-amber-100">
          {slot.name}
        </span>
        <span className="shrink-0 font-mono text-xs text-amber-700 dark:text-amber-300">
          {slot.startTime} - {slot.endTime}
        </span>
      </div>
      <div className="mt-1 flex items-center gap-2 text-[11px] text-amber-700/90 dark:text-amber-300/90">
        <span>Used {slot.usageCount}x</span>
        <span>•</span>
        <span>{formatPrefillDate(slot.lastUsedAt)}</span>
      </div>
    </button>
  )
}

export interface CreateAssignmentModalProps {
  companyId: string
  companyOptions?: Array<{ companyId: string; companyName: string }>
  open: boolean
  onClose: () => void
  prefill?: { userId: string; date: string }
}

export function CreateAssignmentModal({
  companyId,
  companyOptions = [],
  open,
  onClose,
  prefill,
}: CreateAssignmentModalProps): React.ReactElement {
  const formId = useId()
  const [selectedCompanyId, setSelectedCompanyId] = useState(companyId)
  const createAssignment = useCreateShiftAssignment()
  const bulkAssign = useBulkAssignShifts()
  const { data: organization } = useCurrentOrganization()
  const { data: shifts = [] } = useShiftTemplates(open ? selectedCompanyId : null)
  const { data: recentCustomSlots } = useRecentCustomShiftSlots(selectedCompanyId, {
    enabled: !!selectedCompanyId && open,
  })
  const { data: membersPage } = useCompanyMembers(open ? selectedCompanyId : null, {
    limit: 100,
    page: 1,
  })
  const members = useMemo(() => membersPage?.data ?? [], [membersPage?.data])
  const { data: myCompanies = [] } = useMyCompanies()
  const currentCompany = useMemo(
    () => myCompanies.find((c) => c._id === selectedCompanyId),
    [myCompanies, selectedCompanyId]
  )

  const shiftOptions = shifts.map((s) => ({
    value: s._id,
    label: `${s.name} (${s.startTime} - ${s.endTime})`,
  }))
  const userOptions = members.map((member) => ({
    value: getMemberId(member),
    label: getMemberLabel(member),
    description: getMemberSubline(member),
  }))

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    clearErrors,
    control,
    formState: { errors },
    setError,
    getFieldState,
  } = useForm<CreateShiftAssignmentFormData>({
    resolver: zodResolver(createShiftAssignmentSchema) as Resolver<CreateShiftAssignmentFormData>,
    mode: 'onChange',
    shouldUnregister: false,
    defaultValues: {
      assignmentMode: 'custom',
      status: 'scheduled',
      shiftId: '',
      userId: '',
      date: '',
      notes: '',
      customSlot: {
        name: '',
        startTime: '',
        endTime: '',
        gracePeriodMinutes: 0,
        maxEarlyClockInMinutes: 10,
        allowClockInPastEnd: false,
        lateClockInCutoffMinutes: 0,
        breakAllowances: [],
        color: '',
        description: '',
      },
    },
  })
  const {
    fields: breakFields,
    append: appendBreak,
    remove: removeBreak,
  } = useFieldArray({
    control,
    name: 'customSlot.breakAllowances',
  })

  const [editingPrefill, setEditingPrefill] = useState(false)
  /** Set when Assign is pressed without a company (only pickable when several exist). */
  const [companyMissing, setCompanyMissing] = useState(false)
  const [nameTouched, setNameTouched] = useState(false)
  const [repeatWeekdays, setRepeatWeekdays] = useState(false)
  const [selectedRepeatDates, setSelectedRepeatDates] = useState<string[]>([])
  const lastAutoSlotName = useRef('')
  const isPrefilled = !!prefill && !editingPrefill
  const watchedUserId = watch('userId')
  const watchedDate = watch('date')
  const watchedShiftId = watch('shiftId') ?? ''
  const customSlotName = watch('customSlot.name') ?? ''
  const customStartTime = watch('customSlot.startTime') ?? ''
  const customEndTime = watch('customSlot.endTime') ?? ''
  const allowClockInPastEnd = watch('customSlot.allowClockInPastEnd') ?? false
  const nameRegister = register('customSlot.name')
  const todayInputValue = useMemo(() => toDateInputValue(new Date()), [])
  const temporalErrorMessage = useMemo(() => {
    if (!watchedDate) return ''
    if (watchedDate < todayInputValue) {
      return 'Shift date must be today or later'
    }
    if (!customStartTime) return ''
    return isShiftStartInPast(watchedDate, customStartTime)
      ? 'Shift start time must be current or future'
      : ''
  }, [customStartTime, todayInputValue, watchedDate])
  const hasTemporalError = temporalErrorMessage !== ''
  const assignBlockedReason = useMemo(() => {
    if (!hasTemporalError) return ''
    if (watchedDate < todayInputValue) {
      return 'Cannot assign this shift because the selected date is in the past. Choose today or a future date.'
    }
    return 'Cannot assign this shift because the start time has already passed. Choose a current or future start time.'
  }, [hasTemporalError, todayInputValue, watchedDate])
  const organizationWorkWeek = useMemo(() => {
    const days = organization?.settings?.workWeek ?? []
    return days.length > 0 ? days : ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
  }, [organization?.settings?.workWeek])
  const repeatDateOptions = useMemo(
    () =>
      remainingDatesThisWeek(watchedDate).filter((date) =>
        organizationWorkWeek.includes(getWeekdayName(date))
      ),
    [organizationWorkWeek, watchedDate]
  )
  const defaultRepeatDates = useMemo(
    () => defaultRepeatDatesForWorkWeek(repeatDateOptions, organizationWorkWeek),
    [organizationWorkWeek, repeatDateOptions]
  )
  const canRepeatWeekdays = repeatDateOptions.length > 0
  const activeRepeatDates = selectedRepeatDates
  const isRepeatActive = repeatWeekdays && canRepeatWeekdays && activeRepeatDates.length > 0
  const submitCount = isRepeatActive ? activeRepeatDates.length : 1
  const repeatSelectionMissing = repeatWeekdays && activeRepeatDates.length === 0
  const isSubmitting = createAssignment.isPending || bulkAssign.isPending

  useEffect(() => {
    if (!open) return
    setSelectedCompanyId(companyId)
    setCompanyMissing(false)
    setNameTouched(false)
    setRepeatWeekdays(false)
    setSelectedRepeatDates([])
    lastAutoSlotName.current = ''

    reset({
      assignmentMode: 'custom',
      status: 'scheduled',
      shiftId: '',
      userId: prefill?.userId ?? '',
      date: prefill?.date ?? '',
      notes: '',
      customSlot: {
        name: '',
        startTime: '',
        endTime: '',
        gracePeriodMinutes: 0,
        maxEarlyClockInMinutes: 10,
        allowClockInPastEnd: false,
        lateClockInCutoffMinutes: 0,
        breakAllowances: [],
        color: '',
        description: '',
      },
    })

    if (prefill) {
      clearErrors(['userId', 'date'])
    }
  }, [open, companyId, prefill, reset, clearErrors])

  useEffect(() => {
    if (!repeatWeekdays) return
    setSelectedRepeatDates(defaultRepeatDates)
  }, [defaultRepeatDates, repeatWeekdays])

  useEffect(() => {
    if (!temporalErrorMessage) {
      // Clear only the temporal errors this effect set ('validate'); a
      // required-field error from a submit attempt stays until fixed.
      const ownErrors = (['date', 'shiftId', 'customSlot.startTime'] as const).filter(
        (name) => getFieldState(name).error?.type === 'validate'
      )
      if (ownErrors.length > 0) clearErrors([...ownErrors])
      return
    }

    if (watchedDate < todayInputValue) {
      setError('date', { type: 'validate', message: temporalErrorMessage })
      return
    }

    setError('customSlot.startTime', {
      type: 'validate',
      message: temporalErrorMessage,
    })
  }, [clearErrors, getFieldState, setError, temporalErrorMessage, todayInputValue, watchedDate])

  const prefilledUserName = useMemo(() => {
    if (!prefill) return ''
    const member = members.find((item) => getMemberId(item) === prefill.userId)
    return member ? getMemberLabel(member) : ''
  }, [prefill, members])

  const applyTemplate = (shiftId: string): void => {
    setValue('shiftId', shiftId)
    const shift = shifts.find((s) => s._id === shiftId)
    if (!shift) return

    setNameTouched(false)
    lastAutoSlotName.current = shift.name
    setValue('customSlot.name', shift.name, { shouldValidate: true })
    setValue('customSlot.startTime', shift.startTime, { shouldValidate: true })
    setValue('customSlot.endTime', shift.endTime, { shouldValidate: true })
    setValue('customSlot.gracePeriodMinutes', shift.gracePeriodMinutes ?? 0, {
      shouldValidate: true,
    })
    setValue('customSlot.maxEarlyClockInMinutes', shift.maxEarlyClockInMinutes ?? 10, {
      shouldValidate: true,
    })
    setValue('customSlot.allowClockInPastEnd', shift.allowClockInPastEnd ?? false, {
      shouldValidate: true,
    })
    setValue('customSlot.lateClockInCutoffMinutes', shift.lateClockInCutoffMinutes ?? 0, {
      shouldValidate: true,
    })
    setValue('customSlot.breakAllowances', shift.breakAllowances ?? [], {
      shouldValidate: true,
    })
    setValue('customSlot.color', shift.color ?? '')
    setValue('customSlot.description', shift.description ?? '')
    clearErrors(['customSlot', 'shiftId'])
  }

  const applyRecentSlot = (slot: RecentCustomShiftSlot): void => {
    setValue('shiftId', '')
    setNameTouched(false)
    lastAutoSlotName.current = slot.name
    setValue('customSlot.name', slot.name, { shouldValidate: true })
    setValue('customSlot.startTime', slot.startTime, { shouldValidate: true })
    setValue('customSlot.endTime', slot.endTime, { shouldValidate: true })
    setValue('customSlot.gracePeriodMinutes', slot.gracePeriodMinutes ?? 0, {
      shouldValidate: true,
    })
    setValue('customSlot.maxEarlyClockInMinutes', slot.maxEarlyClockInMinutes ?? 10, {
      shouldValidate: true,
    })
    setValue('customSlot.allowClockInPastEnd', slot.allowClockInPastEnd ?? false, {
      shouldValidate: true,
    })
    setValue('customSlot.lateClockInCutoffMinutes', slot.lateClockInCutoffMinutes ?? 0, {
      shouldValidate: true,
    })
    setValue('customSlot.breakAllowances', slot.breakAllowances ?? [], {
      shouldValidate: true,
    })
    setValue('customSlot.color', slot.color ?? '')
    setValue('customSlot.description', slot.description ?? '')
  }

  const autoSlotName = useMemo(() => {
    if (!watchedUserId) return ''
    const member = members.find((item) => getMemberId(item) === watchedUserId)
    const user = member && typeof member.userId !== 'string' ? member.userId : undefined
    const first =
      user?.firstName?.trim() ||
      (member ? getMemberLabel(member).trim().split(/\s+/)[0] : '') ||
      user?.email?.split('@')[0] ||
      ''
    if (!first) return ''
    const codePart = currentCompany?.code?.trim() ? `-${currentCompany.code.trim()}` : ''
    const base = `${first}${codePart}`
    if (customStartTime && customEndTime) {
      return `${base} ${customStartTime}-${customEndTime}`
    }
    return base
  }, [watchedUserId, customStartTime, customEndTime, members, currentCompany?.code])

  useEffect(() => {
    if (nameTouched) return
    if (!autoSlotName) return
    if (customSlotName && customSlotName !== lastAutoSlotName.current) return
    setValue('customSlot.name', autoSlotName, { shouldValidate: true })
    lastAutoSlotName.current = autoSlotName
  }, [autoSlotName, customSlotName, nameTouched, setValue])

  const handleClose = (): void => {
    setEditingPrefill(false)
    setRepeatWeekdays(false)
    setSelectedRepeatDates([])
    onClose()
  }

  const resetFormValues = (): void => {
    setEditingPrefill(false)
    setNameTouched(false)
    setRepeatWeekdays(false)
    setSelectedRepeatDates([])
    lastAutoSlotName.current = ''
    reset({
      assignmentMode: 'custom',
      status: 'scheduled',
      shiftId: '',
      userId: prefill?.userId ?? '',
      date: prefill?.date ?? '',
      notes: '',
      customSlot: {
        name: '',
        startTime: '',
        endTime: '',
        gracePeriodMinutes: 0,
        maxEarlyClockInMinutes: 10,
        allowClockInPastEnd: false,
        lateClockInCutoffMinutes: 0,
        breakAllowances: [],
        color: '',
        description: '',
      },
    })
    clearErrors()
  }

  const [backfillConfirm, setBackfillConfirm] = useState<{
    payload: Parameters<typeof createAssignment.mutate>[0]['payload']
    shiftEndAt?: string
    date?: string
  } | null>(null)

  const submitMutation = (
    payload: Parameters<typeof createAssignment.mutate>[0]['payload']
  ): void => {
    createAssignment.mutate(
      { companyId: selectedCompanyId, payload },
      {
        onSuccess: () => {
          reset()
          setBackfillConfirm(null)
          handleClose()
        },
        onError: (error) => {
          const axiosErr = error as AxiosError<{
            code?: string
            data?: { shiftEndAt?: string; date?: string }
          }>
          const body = axiosErr.response?.data
          if (body?.code === 'ASSIGNMENT_IN_THE_PAST' && !payload.allowBackfill) {
            setBackfillConfirm({
              payload,
              shiftEndAt: body.data?.shiftEndAt,
              date: body.data?.date,
            })
          }
        },
      }
    )
  }

  const onSubmit = (data: CreateShiftAssignmentFormData): void => {
    const customSlot = toCustomSlotPayload(data)
    // When the slot was seeded from a template, keep the shiftId so the backend
    // links the assignment back to it (shiftSourceType 'template' +
    // templateSnapshot). This is what lets later template edits propagate to
    // this shift. The customSlot overrides still ride along.
    const templateShiftId = data.shiftId || undefined

    if (isRepeatActive) {
      bulkAssign.mutate(
        {
          companyId: selectedCompanyId,
          suppressSuccessToast: true,
          payload: {
            shiftId: templateShiftId,
            userIds: [data.userId],
            dates: activeRepeatDates,
            customSlot,
          },
        },
        {
          onSuccess: (result) => {
            const summary = getBulkAssignSummary(
              result,
              activeRepeatDates.length,
              Object.fromEntries(userOptions.map((option) => [option.value, option.label]))
            )
            reset()
            setBackfillConfirm(null)
            handleClose()
            if (summary.kind === 'success') {
              toast.success(summary.message)
            } else {
              toast.warning(summary.message)
            }
          },
        }
      )
      return
    }

    submitMutation({
      shiftId: templateShiftId,
      userId: data.userId,
      date: data.date,
      notes: data.notes || undefined,
      customSlot,
    })
  }

  const confirmBackfill = (): void => {
    if (!backfillConfirm) return
    submitMutation({ ...backfillConfirm.payload, allowBackfill: true })
  }

  const backfillMessage = backfillConfirm
    ? `This shift ended ${
        backfillConfirm.shiftEndAt
          ? `on ${format(new Date(backfillConfirm.shiftEndAt), 'EEE, d MMM yyyy HH:mm')}`
          : backfillConfirm.date
            ? `on ${backfillConfirm.date}`
            : 'in the past'
      }. Create it as a backfill assignment? Requires backfill permission.`
    : ''

  if (!open) return <></>

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Assign shift"
      className="max-w-4xl"
      footer={
        <>
          {assignBlockedReason && (
            <p
              id={`${formId}-assign-blocked-reason`}
              role="alert"
              className="mr-auto max-w-md text-sm font-medium text-red-600 dark:text-red-400"
            >
              {assignBlockedReason}
            </p>
          )}
          <Button type="button" variant="secondary" onClick={resetFormValues}>
            Reset
          </Button>
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            form={formId}
            type="submit"
            variant="primary"
            aria-busy={isSubmitting}
            aria-describedby={assignBlockedReason ? `${formId}-assign-blocked-reason` : undefined}
            // Empty required fields don't disable Assign: pressing it runs
            // validation and shows each field's error. Only states that
            // already explain themselves (past date/time, no repeat day) do.
            disabled={hasTemporalError || repeatSelectionMissing}
            loading={isSubmitting}
          >
            {isSubmitting
              ? submitCount > 1
                ? `Assigning ${submitCount} shifts…`
                : 'Assigning…'
              : submitCount > 1
                ? `Assign ${submitCount} shifts`
                : 'Assign'}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        noValidate
        onSubmit={(event) => {
          setCompanyMissing(!selectedCompanyId)
          return handleSubmit((data) => {
            if (!selectedCompanyId) return
            onSubmit(data)
          })(event)
        }}
        className="space-y-5"
      >
        <input type="hidden" {...register('userId')} />
        <input type="hidden" {...register('date')} />

        <div className={companyOptions.length > 1 ? 'grid gap-4 md:grid-cols-2' : ''}>
          {companyOptions.length > 1 && (
            <div>
              <Select
                label="Company"
                required
                options={companyOptions.map((company) => ({
                  value: company.companyId,
                  label: company.companyName,
                }))}
                value={selectedCompanyId}
                onChange={(nextCompanyId) => {
                  setSelectedCompanyId(nextCompanyId)
                  setCompanyMissing(false)
                  setValue('shiftId', '')
                }}
                placeholder="Select the company for this shift"
                clearable={false}
              />
              {companyMissing && !selectedCompanyId && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">Company is required</p>
              )}
              {!selectedCompanyId && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  This employee belongs to multiple companies. Choose where to assign the shift.
                </p>
              )}
            </div>
          )}

          {isPrefilled && prefill ? (
            <div className="flex min-h-20 items-start justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800/60">
              <div className="flex min-w-0 items-center gap-3">
                <Avatar name={prefilledUserName || 'User'} size="sm" />
                <div className="min-w-0 text-sm">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
                    Employee <span className="text-danger">*</span>
                  </div>
                  <div className="truncate font-medium text-gray-900 dark:text-gray-100">
                    {prefilledUserName || 'Selected user'}
                  </div>
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Date <span className="text-danger">*</span> · {formatPrefillDate(prefill.date)}
                  </div>
                  {errors.date && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                      {errors.date.message}
                    </p>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setEditingPrefill(true)}
                className="shrink-0 text-xs font-medium text-primary hover:underline"
              >
                Change
              </button>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Employee <span className="text-danger">*</span>
                </label>
                <Select
                  required
                  options={[{ value: '', label: 'Select user' }, ...userOptions]}
                  value={watch('userId')}
                  onChange={(v) => setValue('userId', v, { shouldValidate: true })}
                />
                {errors.userId && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                    {errors.userId.message}
                  </p>
                )}
              </div>
              <DateInput
                label="Date"
                required
                min={todayInputValue}
                {...register('date')}
                error={errors.date?.message}
              />
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-gray-200 p-4 dark:border-gray-700">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Slot source
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Use a saved template or enter a one-off custom time slot.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Use template
              </label>
              <Select
                options={[{ value: '', label: 'Start from scratch' }, ...shiftOptions]}
                value={watchedShiftId}
                onChange={applyTemplate}
                clearable={false}
              />
            </div>

            {!!recentCustomSlots?.length && (
              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-amber-700 dark:text-amber-300">
                  Recent custom slots
                </div>
                <div className="grid gap-2 md:grid-cols-2">
                  {recentCustomSlots.slice(0, 4).map((slot) => (
                    <RecentSlotButton
                      key={`${slot.name}-${slot.startTime}-${slot.endTime}`}
                      slot={slot}
                      onApply={applyRecentSlot}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Slot name"
                required
                {...nameRegister}
                onChange={(e) => {
                  setNameTouched(true)
                  void nameRegister.onChange(e)
                }}
                error={errors.customSlot?.name?.message}
                placeholder={autoSlotName || 'e.g. Custom evening'}
                hint="Auto-filled from user + time. Edit to override."
              />
              <Input
                label="Grace minutes"
                type="number"
                min={0}
                {...register('customSlot.gracePeriodMinutes', { valueAsNumber: true })}
                error={errors.customSlot?.gracePeriodMinutes?.message}
              />
              <TimeInput
                label="Start time"
                required
                value={customStartTime}
                onChange={(v) => setValue('customSlot.startTime', v, { shouldValidate: true })}
                error={errors.customSlot?.startTime?.message}
              />
              <TimeInput
                label="End time"
                required
                value={customEndTime}
                onChange={(v) => setValue('customSlot.endTime', v, { shouldValidate: true })}
                error={errors.customSlot?.endTime?.message}
              />
              <CustomSlotDurationHint startTime={customStartTime} endTime={customEndTime} />
            </div>

            <fieldset className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
              <legend className="px-1 text-sm font-semibold text-gray-700 dark:text-gray-300">
                Clock-in window
              </legend>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Beyond these limits, staff must request a time correction.
              </p>
              <div className="mt-3 grid items-end gap-4 md:grid-cols-2">
                <Input
                  label="Allow early clock-in (minutes before start)"
                  type="number"
                  min={0}
                  {...register('customSlot.maxEarlyClockInMinutes', { valueAsNumber: true })}
                  error={errors.customSlot?.maxEarlyClockInMinutes?.message}
                />
                <Input
                  label="Automatic clock-out cutoff (minutes after end)"
                  type="number"
                  min={0}
                  {...register('customSlot.lateClockInCutoffMinutes', { valueAsNumber: true })}
                  error={errors.customSlot?.lateClockInCutoffMinutes?.message}
                  disabled={allowClockInPastEnd === true}
                />
              </div>
              <label className="mt-3 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  {...register('customSlot.allowClockInPastEnd')}
                />
                Allow clock-in after shift ends
              </label>
            </fieldset>

            <div className="flex flex-wrap items-center gap-3">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Color</label>
              <input
                type="color"
                {...register('customSlot.color')}
                className="h-8 w-12 cursor-pointer rounded border border-gray-300 bg-transparent p-0.5 dark:border-gray-600"
                aria-label="Slot color"
              />
            </div>

            <Input
              label="Description"
              {...register('customSlot.description')}
              error={errors.customSlot?.description?.message}
              placeholder="Optional note about this custom slot"
            />

            <div className="border-t border-gray-200 pt-4 dark:border-gray-600">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Break allowances (optional)
                </span>
                <Button
                  type="button"
                  variant="secondary"
                  className="text-sm"
                  onClick={() =>
                    appendBreak({
                      name: '',
                      duration: 0,
                      isPaid: false,
                      isFlexible: true,
                      scheduledStartTime: '',
                    })
                  }
                >
                  <Plus className="mr-1 h-4 w-4" aria-hidden />
                  Add break
                </Button>
              </div>
              {breakFields.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No breaks. Add optional lunch or rest breaks.
                </p>
              )}
              {breakFields.map((field, index) => (
                <div
                  key={field.id}
                  className="mb-3 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-600 dark:bg-gray-800/50"
                >
                  <div className="mb-2 flex justify-end">
                    <button
                      type="button"
                      onClick={() => removeBreak(index)}
                      className="text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                      aria-label={`Remove break ${index + 1}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="grid gap-3">
                    <Input
                      label="Break name"
                      required
                      {...register(`customSlot.breakAllowances.${index}.name`)}
                      error={errors.customSlot?.breakAllowances?.[index]?.name?.message}
                      placeholder="e.g. Lunch Break"
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        label="Duration (min)"
                        required
                        type="number"
                        min={0}
                        {...register(`customSlot.breakAllowances.${index}.duration`, {
                          valueAsNumber: true,
                        })}
                        error={errors.customSlot?.breakAllowances?.[index]?.duration?.message}
                      />
                      <Controller
                        control={control}
                        name={`customSlot.breakAllowances.${index}.scheduledStartTime`}
                        render={({ field: scheduledStartField }) => (
                          <TimeInput
                            label="Scheduled start (optional)"
                            value={scheduledStartField.value ?? ''}
                            onChange={scheduledStartField.onChange}
                            onBlur={scheduledStartField.onBlur}
                            name={scheduledStartField.name}
                          />
                        )}
                      />
                    </div>
                    <div className="flex flex-wrap gap-4">
                      <Toggle
                        label="Paid break"
                        checked={watch(`customSlot.breakAllowances.${index}.isPaid`) ?? false}
                        onChange={(checked) =>
                          setValue(`customSlot.breakAllowances.${index}.isPaid`, checked, {
                            shouldValidate: true,
                          })
                        }
                      />
                      <Toggle
                        label="Flexible"
                        checked={watch(`customSlot.breakAllowances.${index}.isFlexible`) ?? true}
                        onChange={(checked) =>
                          setValue(`customSlot.breakAllowances.${index}.isFlexible`, checked, {
                            shouldValidate: true,
                          })
                        }
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <Input label="Notes (optional)" {...register('notes')} error={errors.notes?.message} />

        <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 dark:border-gray-700 dark:bg-gray-800/50">
          <label className="flex items-start gap-3 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
              checked={repeatWeekdays}
              disabled={!canRepeatWeekdays}
              onChange={(event) => {
                setRepeatWeekdays(event.target.checked)
                setSelectedRepeatDates(event.target.checked ? defaultRepeatDates : [])
              }}
            />
            <span className="min-w-0">
              <span className="block font-medium text-gray-900 dark:text-gray-100">
                Repeat for selected days this week
              </span>
              {isRepeatActive ? (
                <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                  Will assign {activeRepeatDates.map(formatRepeatPreviewDate).join(', ')}
                </span>
              ) : repeatSelectionMissing ? (
                <span className="mt-0.5 block text-xs text-red-600 dark:text-red-400">
                  Select at least one day to repeat this shift.
                </span>
              ) : (
                <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                  {watchedDate && !canRepeatWeekdays
                    ? 'No organization working days remain this week.'
                    : 'Uses the working days configured in organization settings.'}
                </span>
              )}
            </span>
          </label>
          {repeatWeekdays && repeatDateOptions.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2 pl-7">
              {repeatDateOptions.map((date) => {
                const checked = activeRepeatDates.includes(date)
                return (
                  <button
                    key={date}
                    type="button"
                    onClick={() =>
                      setSelectedRepeatDates((current) => {
                        return current.includes(date)
                          ? current.filter((item) => item !== date)
                          : [...current, date].sort()
                      })
                    }
                    className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                      checked
                        ? 'border-primary bg-primary text-white'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-primary/60 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100'
                    }`}
                    aria-pressed={checked}
                  >
                    {formatRepeatPreviewDate(date)}
                  </button>
                )
              })}
            </div>
          )}
        </div>
      </form>

      <ConfirmDialog
        open={!!backfillConfirm}
        onClose={() => setBackfillConfirm(null)}
        onConfirm={confirmBackfill}
        title="Shift already ended"
        message={backfillMessage}
        confirmLabel="Backfill anyway"
        variant="danger"
        confirmLoading={createAssignment.isPending}
      />
    </Modal>
  )
}

CreateAssignmentModal.displayName = 'CreateAssignmentModal'

const HH_MM_RE = /^([01]\d|2[0-3]):([0-5]\d)$/

function CustomSlotDurationHint({
  startTime,
  endTime,
}: {
  startTime?: string
  endTime?: string
}): React.ReactElement | null {
  if (!startTime || !endTime) return null
  if (!HH_MM_RE.test(startTime) || !HH_MM_RE.test(endTime)) return null
  const [sh, sm] = startTime.split(':').map(Number)
  const [eh, em] = endTime.split(':').map(Number)
  const startMin = sh * 60 + sm
  let endMin = eh * 60 + em
  if (endMin === startMin) return null
  const crossesMidnight = endMin < startMin
  if (crossesMidnight) endMin += 1440
  const duration = endMin - startMin
  const h = Math.floor(duration / 60)
  const m = duration % 60
  const durationText = m === 0 ? `${h}h` : `${h}h ${m}m`
  return (
    <p className="-mt-2 text-xs text-gray-500 dark:text-gray-400">
      Duration: <span className="font-medium text-gray-700 dark:text-gray-300">{durationText}</span>
      {crossesMidnight && (
        <span className="ml-2 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-200">
          Ends next day
        </span>
      )}
    </p>
  )
}
